// Build phase nest in the ConceptRail (Piece C, brainstorm "Option 1.5"):
// plain view links + one muted inline count each. Views are places, not
// jobs — deliberately NO lifecycle badges here (those would lie). Locks
// only where a real gate exists (Split: debate map; Write: paid plan).
import { Link, useSearch } from "@tanstack/react-router";
import { Columns2, Grid3X3, LayoutDashboard, Map, PenLine, Scale } from "lucide-react";
import { effectiveBuildView, type BuildViewParam } from "@/lib/buildNextStep";
import { useClaims } from "@/hooks/useClaims";
import { useFieldDebate } from "@/hooks/useFieldDebate";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { useWritingDraft } from "@/hooks/useWritingDraft";
import { trackEvent } from "@/lib/engagement";
import { useAnalyzeToolState } from "@/hooks/useAnalyzeToolState";
import { STATUS_BADGE } from "@/components/analyze/toolMeta";

type BuildView = BuildViewParam;

interface Props {
  conceptId: string;
  /** Collapsed-rail mode: vertical icon stack instead of labeled rows.
      The Build views have NO other entry point once the rail collapses
      (the in-page toggle is gone), so the strip must keep them reachable. */
  iconOnly?: boolean;
}

export function BuildViewNest({ conceptId, iconOnly = false }: Props) {
  const search = useSearch({ strict: false }) as {
    view?: BuildView;
    critique?: boolean;
  };
  const { data: claimsData } = useClaims(conceptId);
  // Mirror the route's smart default (first visit → Overview, else Map) so
  // the active row matches what the page actually renders.
  const view: BuildView =
    effectiveBuildView(
      search.view,
      claimsData === undefined ? undefined : claimsData.length,
    ) ?? "map";
  const { data: debateMap } = useFieldDebate(conceptId);
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);
  // Pro-gated endpoint — don't fire the query for free users.
  const { draft } = useWritingDraft(conceptId, { enabled: isPaid });

  const claimCount = claimsData?.length;
  const evidencedCount = claimsData?.filter((c) => c.evidence_count > 0).length;
  const hasDraft = !!draft?.content;

  // Aggregate critique status for the ambient chip. The views above stay
  // badge-free (places, not jobs) — this entry IS a job, hence the divider.
  const { critique } = useAnalyzeToolState(conceptId);
  const critiqueStatus = critique.aggregate;
  const critiqueLocked = critiqueStatus.status === "locked";

  const rows: {
    key: BuildView;
    label: string;
    icon: React.ReactNode;
    meta: string;
    lockedReason?: string;
    guideTarget?: string;
    onClick?: () => void;
  }[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <LayoutDashboard className="size-3.5" />,
      meta: "",
    },
    {
      key: "map",
      label: "Map",
      icon: <Map className="size-3.5" />,
      meta: claimCount == null ? "—" : `${claimCount} claims`,
    },
    {
      key: "matrix",
      label: "Matrix",
      icon: <Grid3X3 className="size-3.5" />,
      meta:
        claimCount == null ? "—" : `${evidencedCount ?? 0}/${claimCount} evidenced`,
    },
    {
      key: "split",
      label: "Split",
      icon: <Columns2 className="size-3.5" />,
      meta: "map + debate",
      lockedReason: debateMap ? undefined : "needs Field Debate",
    },
    {
      key: "write",
      label: "Write",
      icon: <PenLine className="size-3.5" />,
      meta: isPaid ? (hasDraft ? "draft · started" : "no draft") : "",
      lockedReason: isPaid ? undefined : "paid plan",
      guideTarget: "writingWorkspaceButton",
      onClick: () =>
        trackEvent("feature_use", { feature: "writing", action: "started" }),
    },
  ];

  if (iconOnly) {
    return (
      <div className="flex flex-col items-center gap-1.5">
        {rows.map((r) => {
          const locked = !!r.lockedReason;
          const active = view === r.key;
          if (locked) {
            return (
              <span
                key={r.key}
                title={`${r.label} — 🔒 ${r.lockedReason}`}
                aria-label={`${r.label} (locked: ${r.lockedReason})`}
                className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground/40"
              >
                {r.icon}
              </span>
            );
          }
          return (
            <Link
              key={r.key}
              to="/concepts/$conceptId/build"
              params={{ conceptId }}
              search={{ view: r.key, critique: search.critique }}
              onClick={r.onClick}
              title={r.label}
              aria-label={r.label}
              aria-current={active ? "page" : undefined}
              className={`inline-flex size-7 items-center justify-center rounded-md transition-colors hover:bg-accent/40 ${
                active ? "bg-accent/60 text-foreground" : "text-muted-foreground"
              }`}
            >
              {r.icon}
            </Link>
          );
        })}
        <div className="my-1 h-px w-5 bg-border" />
        {critiqueLocked ? (
          <span
            title={`Argument Critique — 🔒 ${critiqueStatus.lockedReason}`}
            aria-label={`Argument Critique (locked: ${critiqueStatus.lockedReason})`}
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground/40"
          >
            <Scale className="size-3.5" />
          </span>
        ) : (
          <Link
            to="/concepts/$conceptId/build"
            params={{ conceptId }}
            search={{ view, critique: true }}
            title="Argument Critique"
            aria-label="Argument Critique"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/40"
          >
            <Scale className="size-3.5" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="ml-4 mb-2 border-l border-border pl-2.5">
      {rows.map((r) => {
        const locked = !!r.lockedReason;
        const active = view === r.key;
        if (locked) {
          return (
            <div
              key={r.key}
              data-guide-target={r.guideTarget}
              className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground/60"
            >
              <span className="inline-flex size-4 shrink-0 items-center justify-center opacity-50">
                {r.icon}
              </span>
              <span>{r.label}</span>
              <span className="ml-auto shrink-0 text-[9px]">🔒 {r.lockedReason}</span>
            </div>
          );
        }
        return (
          <Link
            key={r.key}
            to="/concepts/$conceptId/build"
            params={{ conceptId }}
            search={{ view: r.key, critique: search.critique }}
            onClick={r.onClick}
            data-guide-target={r.guideTarget}
            aria-current={active ? "page" : undefined}
            className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] transition-colors hover:bg-accent/40 ${
              active ? "bg-accent/60 font-semibold text-foreground" : "text-foreground"
            }`}
          >
            <span className="inline-flex size-4 shrink-0 items-center justify-center text-muted-foreground">
              {r.icon}
            </span>
            <span>{r.label}</span>
            <span className="ml-auto shrink-0 font-mono text-[9px] text-muted-foreground">
              {r.meta}
            </span>
          </Link>
        );
      })}
        <div className="my-1.5 border-t border-border" />
        {critiqueLocked ? (
          <div className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground/60">
            <span className="inline-flex size-4 shrink-0 items-center justify-center opacity-50">
              <Scale className="size-3.5" />
            </span>
            <span>Argument Critique</span>
            <span className="ml-auto shrink-0 text-[9px]">
              🔒 {critiqueStatus.lockedReason}
            </span>
          </div>
        ) : (
          <Link
            to="/concepts/$conceptId/build"
            params={{ conceptId }}
            search={{ view, critique: true }}
            data-guide-target="argumentCritiqueRailEntry"
            className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] text-foreground transition-colors hover:bg-accent/40"
          >
            <span className="inline-flex size-4 shrink-0 items-center justify-center text-muted-foreground">
              <Scale className="size-3.5" />
            </span>
            <span>Argument Critique</span>
            <span
              className={`ml-auto shrink-0 rounded px-1 py-px text-[8px] font-bold tracking-wider ${STATUS_BADGE[critiqueStatus.status].className}`}
            >
              {STATUS_BADGE[critiqueStatus.status].label}
            </span>
          </Link>
        )}
    </div>
  );
}
